/**
 * ApiData - Standardized API Response Handler
 * 
 * Provides consistent interface for handling API responses across the application.
 * Follows JSend specification with extensions for better error handling.
 * 
 * JSend Format:
 * - status: "success", "fail", "error"
 * - data: payload object (null for errors)
 * - message: descriptive message
 * 
 * Usage:
 *   const apiData = new ApiData(response);
 *   if (apiData.isSuccess()) {
 *     const data = apiData.getData();
 *   } else {
 *     const error = apiData.getError();
 *   }
 */

export class ApiData {
  constructor(response) {
    this.response = response;
    this.rawData = response?.data || response;
    // Separate JSend status (string) from HTTP status (number)
    this.jsendStatus = typeof response?.status === 'string' ? response.status.toLowerCase() : null;
    const candidateHttp = response?.statusCode ?? response?.httpStatus ?? (typeof response?.status === 'number' ? response.status : undefined);
    this.httpStatus = typeof candidateHttp === 'number' ? candidateHttp : 0;
    this.topLevelSuccess = typeof response?.success === 'boolean' ? response.success : null;
    
    // Normalize the response structure
    this._normalizeResponse();
  }

  /**
   * Normalize different response formats to a consistent structure
   * Handles both JSend and legacy formats
   */
  _normalizeResponse() {
    // Handle different response formats
    if (this.rawData) {
      // Format 1: { success: boolean, data: any, message: string } - Legacy format
      if (typeof this.rawData.success === 'boolean') {
        this.success = this.rawData.success;
        this.data = this.rawData.data;
        this.message = this.rawData.message || '';
        this.errorMessage = this.rawData.errorMessage || this.rawData.message || '';
        this.jsendStatus = this.success ? 'success' : 'fail';
      }
      // Format 2: { status: "success|fail|error", data: any, message: string } - JSend payload
      // Guard: only treat as JSend if typical JSend keys are present to avoid
      // misinterpreting domain objects that also have a 'status' field
      else if (
        this.rawData.status &&
        typeof this.rawData.status === 'string' &&
        (Object.prototype.hasOwnProperty.call(this.rawData, 'data') ||
         Object.prototype.hasOwnProperty.call(this.rawData, 'message'))
      ) {
        this.jsendStatus = this.rawData.status.toLowerCase();
        this.success = this.jsendStatus === 'success';
        this.data = this.rawData.data;
        this.message = this.rawData.message || '';
        this.errorMessage = this.jsendStatus !== 'success' ? this.rawData.message || '' : '';
      }
      // Format 3: Direct data (assume success if 2xx status)
      else {
        // Determine success from (a) top-level success flag, (b) JSend status, (c) HTTP status
        const httpOk = this.httpStatus >= 200 && this.httpStatus < 300;
        const jsendOk = this.jsendStatus === 'success';
        const topOk = this.topLevelSuccess === true;
        this.success = topOk || jsendOk || httpOk;
        this.jsendStatus = this.jsendStatus || (this.success ? 'success' : 'fail');
        this.data = this.rawData;
        this.message = '';
        this.errorMessage = '';
      }
    } else {
      // No data - determine success from status code
      this.success = this.httpStatus >= 200 && this.httpStatus < 300;
      this.jsendStatus = this.jsendStatus || (this.success ? 'success' : 'error');
      this.data = null;
      this.message = this.response?.message || '';
      this.errorMessage = this.response?.errorMessage || this.response?.message || 'Unknown error';
    }
  }

  /**
   * Check if the API call was successful
   */
  isSuccess() {
    return this.success === true;
  }

  /**
   * Check if the API call failed
   */
  isError() {
    return !this.isSuccess();
  }

  /**
   * Get the JSend status (success, fail, error)
   */
  getStatus() {
    return this.jsendStatus;
  }

  /**
   * Get the response data
   */
  getData(defaultValue = null) {
    if (!this.isSuccess()) return defaultValue;
    
    // Handle nested data structure where actual data is in data.data
    // This is common in Spring Boot responses with JSend format
    if (this.data && typeof this.data === 'object' && this.data.data !== undefined) {
      return this.data.data;
    }
    
    return this.data;
  }

  /**
   * Get error information
   */
  getError() {
    return {
      message: this.errorMessage || this.message || 'Unknown error',
      status: this.status,
      code: this.rawData?.code || this.rawData?.errorCode,
      details: this.rawData?.details || this.rawData?.error
    };
  }

  /**
   * Get success/error message
   */
  getMessage() {
    return this.message || '';
  }

  /**
   * Get HTTP status code
   */
  getHttpStatus() {
    return this.httpStatus;
  }

  /**
   * Get raw response data
   */
  getRawData() {
    return this.rawData;
  }

  /**
   * Transform data using a mapper function
   */
  map(mapper) {
    if (this.isSuccess() && this.data) {
      return mapper(this.data);
    }
    return null;
  }

  /**
   * Execute callback if successful
   */
  onSuccess(callback) {
    if (this.isSuccess()) {
      callback(this.data, this.message);
    }
    return this;
  }

  /**
   * Execute callback if error
   */
  onError(callback) {
    if (this.isError()) {
      callback(this.getError());
    }
    return this;
  }

  /**
   * Get paginated data with metadata
   */
  getPaginatedData() {
    if (!this.isSuccess()) return null;
    
    // Handle different pagination formats
    if (this.data?.content && this.data?.totalElements !== undefined) {
      // Spring Boot Page format
      return {
        items: this.data.content,
        totalItems: this.data.totalElements,
        totalPages: this.data.totalPages,
        currentPage: this.data.number,
        pageSize: this.data.size,
        hasNext: !this.data.last,
        hasPrevious: !this.data.first
      };
    } else if (Array.isArray(this.data)) {
      // Simple array format
      return {
        items: this.data,
        totalItems: this.data.length,
        totalPages: 1,
        currentPage: 0,
        pageSize: this.data.length,
        hasNext: false,
        hasPrevious: false
      };
    }
    
    return this.data;
  }

  /**
   * Static factory methods for common use cases
   */
  static success(data, message = 'Success') {
    return new ApiData({
      status: 200,
      data: { 
        status: 'success', 
        data, 
        message 
      }
    });
  }

  static fail(message, status = 400, code = null) {
    return new ApiData({
      status,
      data: { 
        status: 'fail', 
        message, 
        code 
      }
    });
  }

  static error(message, status = 500, code = null) {
    return new ApiData({
      status,
      data: { 
        status: 'error', 
        message, 
        code 
      }
    });
  }

  /**
   * Handle async API calls with consistent error handling
   */
  static async from(apiCall) {
    try {
      const response = await apiCall();
      return new ApiData(response);
    } catch (error) {
      return new ApiData({
        status: error.response?.status || 0,
        data: {
          status: 'error',
          message: error.message || 'Network error',
          code: error.code || 'NETWORK_ERROR'
        }
      });
    }
  }
}

export default ApiData;
